import { createContext, useContext, useReducer, ReactNode, FC } from 'react';
import {
  FinancialReportsState,
  FinancialReportsAction,
  FinancialReportsActionType,
  FinancialReportsContextType,
  ReportTab,
  ReportPeriod,
  Department,
  SummaryData,
  PendingFee,
  ExpenseCategory,
  Scholarship,
  BudgetItem,
  ComplianceReport,
  AuditReport,
  PaymentStatus,
  ScholarshipStatus,
  BudgetStatus,
} from './financialReports.interfaces';

// Initial mock data
const initialSummaryData: SummaryData = {
  totalIncomeYTD: 4850000,
  totalIncomeMonthly: 485000,
  totalExpenditureYTD: 3200000,
  totalExpenditureMonthly: 320000,
  netSurplus: 1650000,
  outstandingFeesCount: 45,
  outstandingFeesAmount: 225000,
  scholarshipsDisbursed: 180000,
};

const initialPendingFees: PendingFee[] = [
  {
    id: '1',
    studentName: 'Arjun Sharma',
    class: 'Class X-A',
    amountDue: 15000,
    dueDate: '2024-01-15',
    daysOverdue: 5,
    status: PaymentStatus.UNPAID,
  },
  {
    id: '2',
    studentName: 'Priya Patel',
    class: 'Class IX-B',
    amountDue: 12000,
    dueDate: '2024-01-10',
    daysOverdue: 10,
    status: PaymentStatus.UNPAID,
  },
];

const initialExpenseCategories: ExpenseCategory[] = [
  { id: '1', name: 'Staff Salaries', amount: 1800000, color: 'primary' },
  { id: '2', name: 'Utilities', amount: 450000, color: 'secondary' },
  { id: '3', name: 'Maintenance', amount: 380000, color: 'warning' },
  { id: '4', name: 'Transport', amount: 320000, color: 'info' },
  { id: '5', name: 'Events', amount: 150000, color: 'success' },
];

const initialScholarships: Scholarship[] = [
  {
    id: '1',
    studentName: 'Ananya Singh',
    class: 'Class XII-A',
    scholarshipType: 'Merit Scholarship',
    amount: 25000,
    dateAwarded: '2024-04-01',
    status: ScholarshipStatus.DISBURSED,
  },
  {
    id: '2',
    studentName: 'Rahul Kumar',
    class: 'Class XI-B',
    scholarshipType: 'Need-based',
    amount: 20000,
    dateAwarded: '2024-04-15',
    status: ScholarshipStatus.APPROVED,
  },
  {
    id: '3',
    studentName: 'Kavya Reddy',
    class: 'Class X-C',
    scholarshipType: 'Government Grant',
    amount: 15000,
    dateAwarded: '2024-05-01',
    status: ScholarshipStatus.PROCESSING,
  },
];

const initialBudgetItems: BudgetItem[] = [
  {
    id: '1',
    category: 'Staff Salaries',
    budgetedAmount: 2000000,
    actualSpent: 1800000,
    variance: 200000,
    variancePercentage: 10,
    status: BudgetStatus.UNDER_BUDGET,
  },
  {
    id: '2',
    category: 'Infrastructure',
    budgetedAmount: 500000,
    actualSpent: 650000,
    variance: -150000,
    variancePercentage: -30,
    status: BudgetStatus.OVER_BUDGET,
  },
  {
    id: '3',
    category: 'Academic Programs',
    budgetedAmount: 800000,
    actualSpent: 750000,
    variance: 50000,
    variancePercentage: 6.25,
    status: BudgetStatus.UNDER_BUDGET,
  },
];

const initialComplianceReports: ComplianceReport[] = [
  {
    id: '1',
    reportType: 'TDS',
    title: 'TDS Reports',
    description: 'Tax Deducted at Source for staff & vendor payments',
    lastUpdated: 'Jan 2024',
  },
  {
    id: '2',
    reportType: 'PF_ESI',
    title: 'PF & ESI Reports',
    description: 'Employee Provident Fund & ESI contributions',
    lastUpdated: 'Jan 2024',
  },
  {
    id: '3',
    reportType: 'GST',
    title: 'GST Reports',
    description: 'GST reports for canteen, transport & other services',
    lastUpdated: 'Jan 2024',
  },
];

const initialAuditReports: AuditReport[] = [
  {
    id: '1',
    reportType: 'TRIAL_BALANCE',
    title: 'Trial Balance',
    description: 'Current Year Trial Balance Statement',
    academicYear: '2024-25',
    isAvailable: true,
  },
  {
    id: '2',
    reportType: 'PL_STATEMENT',
    title: 'P&L Statement',
    description: 'Profit & Loss Statement showing operational income/expenses',
    academicYear: '2024-25',
    isAvailable: true,
  },
  {
    id: '3',
    reportType: 'BALANCE_SHEET',
    title: 'Balance Sheet',
    description: 'Current Financial Position & Assets/Liabilities',
    academicYear: '2024-25',
    isAvailable: true,
  },
];

// Initial state
const initialState: FinancialReportsState = {
  selectedTab: ReportTab.FEE_COLLECTION,
  filters: {
    academicYear: '2024-25',
    reportPeriod: ReportPeriod.MONTHLY,
    department: Department.ALL,
    searchQuery: '',
  },
  isLoading: false,
  error: null,
  summaryData: initialSummaryData,
  pendingFees: initialPendingFees,
  expenseCategories: initialExpenseCategories,
  scholarships: initialScholarships,
  budgetItems: initialBudgetItems,
  complianceReports: initialComplianceReports,
  auditReports: initialAuditReports,
};

// Reducer function
const financialReportsReducer = (
  state: FinancialReportsState,
  action: FinancialReportsAction
): FinancialReportsState => {
  switch (action.type) {
    case FinancialReportsActionType.SET_SELECTED_TAB:
      return { ...state, selectedTab: action.payload };

    case FinancialReportsActionType.SET_ACADEMIC_YEAR:
      return {
        ...state,
        filters: { ...state.filters, academicYear: action.payload },
      };

    case FinancialReportsActionType.SET_REPORT_PERIOD:
      return {
        ...state,
        filters: { ...state.filters, reportPeriod: action.payload },
      };

    case FinancialReportsActionType.SET_DEPARTMENT:
      return {
        ...state,
        filters: { ...state.filters, department: action.payload },
      };

    case FinancialReportsActionType.SET_SEARCH_QUERY:
      return {
        ...state,
        filters: { ...state.filters, searchQuery: action.payload },
      };

    case FinancialReportsActionType.SET_LOADING:
      return { ...state, isLoading: action.payload };

    case FinancialReportsActionType.SET_ERROR:
      return { ...state, error: action.payload };

    case FinancialReportsActionType.SET_SUMMARY_DATA:
      return { ...state, summaryData: action.payload };

    case FinancialReportsActionType.SET_PENDING_FEES:
      return { ...state, pendingFees: action.payload };

    case FinancialReportsActionType.SET_EXPENSE_CATEGORIES:
      return { ...state, expenseCategories: action.payload };

    case FinancialReportsActionType.SET_SCHOLARSHIPS:
      return { ...state, scholarships: action.payload };

    case FinancialReportsActionType.SET_BUDGET_ITEMS:
      return { ...state, budgetItems: action.payload };

    case FinancialReportsActionType.SET_COMPLIANCE_REPORTS:
      return { ...state, complianceReports: action.payload };

    case FinancialReportsActionType.SET_AUDIT_REPORTS:
      return { ...state, auditReports: action.payload };

    case FinancialReportsActionType.RESET_FILTERS:
      return {
        ...state,
        filters: {
          academicYear: '2024-25',
          reportPeriod: ReportPeriod.MONTHLY,
          department: Department.ALL,
          searchQuery: '',
        },
      };

    default:
      return state;
  }
};

// Create context
const FinancialReportsContext = createContext<
  FinancialReportsContextType | undefined
>(undefined);

// Provider component
interface FinancialReportsProviderProps {
  children: ReactNode;
}

export const FinancialReportsProvider: FC<FinancialReportsProviderProps> = ({
  children,
}) => {
  const [state, dispatch] = useReducer(financialReportsReducer, initialState);

  // Action creators
  const setSelectedTab = (tab: ReportTab) => {
    dispatch({
      type: FinancialReportsActionType.SET_SELECTED_TAB,
      payload: tab,
    });
  };

  const setAcademicYear = (year: string) => {
    dispatch({
      type: FinancialReportsActionType.SET_ACADEMIC_YEAR,
      payload: year,
    });
  };

  const setReportPeriod = (period: ReportPeriod) => {
    dispatch({
      type: FinancialReportsActionType.SET_REPORT_PERIOD,
      payload: period,
    });
  };

  const setDepartment = (department: Department) => {
    dispatch({
      type: FinancialReportsActionType.SET_DEPARTMENT,
      payload: department,
    });
  };

  const setSearchQuery = (query: string) => {
    dispatch({
      type: FinancialReportsActionType.SET_SEARCH_QUERY,
      payload: query,
    });
  };

  const setLoading = (loading: boolean) => {
    dispatch({
      type: FinancialReportsActionType.SET_LOADING,
      payload: loading,
    });
  };

  const setError = (error: string | null) => {
    dispatch({ type: FinancialReportsActionType.SET_ERROR, payload: error });
  };

  const resetFilters = () => {
    dispatch({ type: FinancialReportsActionType.RESET_FILTERS });
  };

  // Utility functions
  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const exportReport = async (
    reportType: string,
    format: string
  ): Promise<void> => {
    try {
      setLoading(true);
      // TODO: Implement actual export logic
      console.log(`Exporting ${reportType} as ${format}`);
      await new Promise((resolve) => setTimeout(resolve, 1000)); // Simulate API call
    } catch (error) {
      setError(`Failed to export ${reportType} report`);
    } finally {
      setLoading(false);
    }
  };

  const sendReportEmail = async (
    reportType: string,
    recipients: string[]
  ): Promise<void> => {
    try {
      setLoading(true);
      // TODO: Implement actual email logic
      console.log(`Sending ${reportType} to:`, recipients);
      await new Promise((resolve) => setTimeout(resolve, 1000)); // Simulate API call
    } catch (error) {
      setError(`Failed to send ${reportType} report via email`);
    } finally {
      setLoading(false);
    }
  };

  const printReport = (reportType: string): void => {
    // TODO: Implement actual print logic
    console.log(`Printing ${reportType} report`);
    window.print();
  };

  const contextValue: FinancialReportsContextType = {
    state,
    dispatch,
    setSelectedTab,
    setAcademicYear,
    setReportPeriod,
    setDepartment,
    setSearchQuery,
    setLoading,
    setError,
    resetFilters,
    formatCurrency,
    exportReport,
    sendReportEmail,
    printReport,
  };

  return (
    <FinancialReportsContext.Provider value={contextValue}>
      {children}
    </FinancialReportsContext.Provider>
  );
};

// Custom hook to use the context
export const useFinancialReports = (): FinancialReportsContextType => {
  const context = useContext(FinancialReportsContext);
  if (context === undefined) {
    throw new Error(
      'useFinancialReports must be used within a FinancialReportsProvider'
    );
  }
  return context;
};

export default FinancialReportsContext;
